/** EPBCS — SLA & Notification setup + Budget Process SLA setup. */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Bell } from "@phosphor-icons/react";
import { Field, Input, Card, Btn } from "./common";

const STATUS_LABELS = {
  first_pass: "First Pass", awaiting_clarification: "Assigned for Clarification",
  under_review: "Under Review",
};
const EMPTY_PROCESS = { name: "", start_date: "", workflow_ids: [] };

export default function SlaSettingsTab() {
  const [sla, setSla] = useState(null);
  const [saving, setSaving] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [draft, setDraft] = useState(EMPTY_PROCESS);

  const load = () => {
    api.get("/epbcs/sla-settings").then((r) => setSla(r.data));
    api.get("/epbcs/budget-processes").then((r) => setProcesses(r.data));
    api.get("/epbcs/workflow-assignments").then((r) => setAssignments(r.data));
  };
  useEffect(load, []);

  const saveSla = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/epbcs/sla-settings", sla);
      setSla(data);
      toast.success("SLA & notification settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const setRule = (status, key, value) => setSla((s) => ({
    ...s, rules: { ...s.rules, [status]: { ...s.rules[status], [key]: Math.max(0, +value || 0) } },
  }));

  const saveProcess = async () => {
    try {
      await api.post("/epbcs/budget-processes", draft);
      toast.success("Budget process saved");
      setDraft(EMPTY_PROCESS);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save process"); }
  };
  const delProcess = async (id) => { await api.delete(`/epbcs/budget-processes/${id}`); load(); };
  const toggleWf = (id) => setDraft((d) => ({
    ...d, workflow_ids: d.workflow_ids.includes(id) ? d.workflow_ids.filter((x) => x !== id) : [...d.workflow_ids, id],
  }));

  if (!sla) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="sla-settings-tab">
      <Card title="SLA & Notification Setup"
        subtitle="Days allotted per status (calculated from when the item entered that status), and how many days before the due date to send a reminder. Escalation emails fire automatically once a due date is missed."
        testid="sla-rules-card"
        actions={
          <label className="flex items-center gap-2 text-[11px] font-semibold text-slate-600">
            <input type="checkbox" checked={sla.weekdays_only} data-testid="sla-weekdays-only"
              onChange={(e) => setSla({ ...sla, weekdays_only: e.target.checked })} />
            Count weekdays only (skip Sat/Sun)
          </label>
        }>
        <table className="text-[12px] w-full">
          <thead>
            <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2">Status</th>
              <th className="py-2">SLA Duration (days)</th>
              <th className="py-2">Reminder Before Due Date (days)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(STATUS_LABELS).map(([status, label]) => (
              <tr key={status} className="border-b border-slate-100" data-testid={`sla-row-${status}`}>
                <td className="py-2 font-semibold">{label}</td>
                <td className="py-2">
                  <Input type="number" min="1" value={sla.rules[status]?.sla_days ?? 3}
                    data-testid={`sla-days-${status}`}
                    onChange={(e) => setRule(status, "sla_days", e.target.value)}
                    className="w-20" />
                </td>
                <td className="py-2">
                  <Input type="number" min="0" value={sla.rules[status]?.remind_before_days ?? 1}
                    data-testid={`sla-remind-${status}`}
                    onChange={(e) => setRule(status, "remind_before_days", e.target.value)}
                    className="w-20" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex justify-end">
          <Btn onClick={saveSla} disabled={saving} data-testid="sla-save">
            <Bell size={12} className="inline mr-1" />{saving ? "Saving…" : "Save SLA settings"}
          </Btn>
        </div>
      </Card>

      <Card title="Budget Process SLA Setup"
        subtitle="Set the budget process start date (SLA clocks start from here) and link the approval workflow(s) that apply."
        testid="budget-process-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Process Name *">
            <Input data-testid="bp-name" value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. FY27 Budget Cycle" />
          </Field>
          <Field label="Start Date *">
            <Input type="date" data-testid="bp-start-date" value={draft.start_date}
              onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
          </Field>
          <Field label="Approval Workflows">
            <div className="flex flex-wrap gap-1.5 pt-1">
              {assignments.length === 0 && <span className="text-[11px] text-slate-400">No workflow assignments yet</span>}
              {assignments.map((a) => (
                <button key={a.id} onClick={() => toggleWf(a.id)} type="button"
                  data-testid={`bp-wf-chip-${a.id}`}
                  className={`px-2 py-1 text-[10px] font-semibold rounded border ${
                    draft.workflow_ids.includes(a.id) ? "border-[#0B1E3F] bg-[#0B1E3F]/5 text-[#0B1E3F]" : "border-slate-200 text-slate-500"}`}>
                  {a.section} · {a.entity || "All"}/{a.division || "All"}
                </button>
              ))}
            </div>
          </Field>
        </div>
        <div className="mt-3 flex justify-end">
          <Btn onClick={saveProcess} data-testid="bp-save" disabled={!draft.name || !draft.start_date}>
            <Plus size={12} className="inline mr-1" />Save budget process
          </Btn>
        </div>
      </Card>

      <Card title={`Budget Processes (${processes.length})`} testid="bp-list">
        <table className="text-[12px] w-full">
          <thead>
            <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2">Name</th><th className="py-2">Start Date</th>
              <th className="py-2">Workflows</th><th className="py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {processes.length === 0 && (
              <tr><td colSpan={4} className="py-5 text-center text-slate-400">No budget processes yet.</td></tr>
            )}
            {processes.map((p) => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`bp-row-${p.id}`}>
                <td className="py-1.5 font-semibold">{p.name}</td>
                <td className="py-1.5">{p.start_date}</td>
                <td className="py-1.5 text-slate-500">{p.workflow_ids.length} linked</td>
                <td className="py-1.5">
                  <button onClick={() => delProcess(p.id)} data-testid={`bp-delete-${p.id}`}
                    className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
